import { Avatar } from "@lazarentertainment/web-design-system/components/avatar"
import {
  LiveSolid,
  PlayCircleSolid,
  VideoSolid,
} from "@lazarentertainment/web-design-system/components/iconSolid"
import { Countdown } from "client/components/channel/Countdown"
import { Badge } from "client/designSystem/Badge"
import { useImageFit } from "client/hooks/useImageFit"
import { useTimeAgo } from "client/hooks/useTimeAgo"
import { formatScheduledDate } from "client/shared/utils/formatScheduledDate"
import { formatTime } from "client/shared/utils/formatTime"
import * as React from "react"

import {
  type LiveShowCardProps,
  type PastShowCardProps,
  type ScheduledShowCardProps,
  ShowCardType,
} from "./types"

import styles from "./ShowCard.module.css"

export type ShowCardProps =
  | PastShowCardProps
  | ScheduledShowCardProps
  | LiveShowCardProps

const CONTAINER_ASPECT_RATIO = 1.6 // ie. 320 / 200
const ASPECT_RATIO_THRESHOLD = 0.3
const DEFAULT_TITLE = "Untitled"
const SHOW_RECENT_MESSAGE_THRESHOLD_MS = 24 * 60 * 60 * 1000 // 24 hours

function PastShowReplayBadge({ endTime }: { endTime?: Date }) {
  let badgeText = "Replay"

  if (endTime) {
    const timeAgo = useTimeAgo(endTime)
    const timeSinceEnd = Date.now() - endTime.getTime()
    if (timeSinceEnd < SHOW_RECENT_MESSAGE_THRESHOLD_MS) {
      badgeText += ` - LIVE ${timeAgo}`
    }
  }

  return (
    <Badge variant="overlay" icon={VideoSolid}>
      {badgeText}
    </Badge>
  )
}

function CardHeader(props: ShowCardProps) {
  if (props.type === ShowCardType.Past) {
    return (
      <>
        <PastShowReplayBadge endTime={props.endTime} />
        <Badge variant="overlay">{formatTime(props.duration)}</Badge>
      </>
    )
  }
  if (props.type === ShowCardType.Scheduled) {
    return <Badge variant="overlay">Upcoming</Badge>
  }
  if (props.type === ShowCardType.Live) {
    return (
      <Badge variant="rose" icon={LiveSolid}>
        Live
      </Badge>
    )
  }
  return null
}

function OverlayContent(props: ShowCardProps & { title: string }) {
  if (props.type === ShowCardType.Scheduled) {
    return (
      <div className={styles.countdownContainer}>
        <Countdown
          variant="overlay"
          countdownDateMs={props.scheduledStartDate.getTime()}
        />
      </div>
    )
  }

  return (
    <div className={styles.overlayFooter}>
      <div className={styles.showInfo}>
        {props.channelAvatarUrl && (
          <Avatar size="sm" className={styles.avatar}>
            {props.channelAvatarUrl && (
              <img
                src={props.channelAvatarUrl}
                alt={props.channelName ?? ""}
              />
            )}
          </Avatar>
        )}
        <span className={styles.showInfoText}>
          <h3 className={styles.showTitle}>{props.title}</h3>
          {props.channelName && (
            <span className={styles.channelName}>{props.channelName}</span>
          )}
        </span>
        <PlayCircleSolid data-size="xl" className={styles.playIcon} />
      </div>
    </div>
  )
}

function Footer(props: ScheduledShowCardProps & { title: string }) {
  return (
    <div className={styles.cardFooter}>
      <div className={styles.scheduledInfo}>
        <div className={styles.scheduledDate}>
          {formatScheduledDate(props.scheduledStartDate)}
        </div>
        <h3 className={styles.showTitle}>{props.title}</h3>
      </div>
      <div className={styles.showInfo}>
        <Avatar size="sm" className={styles.avatar}>
          {props.channelAvatarUrl && (
            <img src={props.channelAvatarUrl} alt={props.channelName} />
          )}
        </Avatar>
        {props.channelName && (
          <span className={styles.channelName}>{props.channelName}</span>
        )}
      </div>
    </div>
  )
}

export default function ShowCard(props: ShowCardProps) {
  const title = props.title || DEFAULT_TITLE
  const thumbnailFitsContainer = useImageFit(
    props.thumbnailUrl,
    CONTAINER_ASPECT_RATIO,
    ASPECT_RATIO_THRESHOLD,
  )

  return (
    <button
      type="button"
      className={styles.card}
      onClick={props.onClick}
      data-fits-container={thumbnailFitsContainer}
      data-show-type={props.type}
      data-full-width={props.fullWidth}
      data-clickable={!!props.onClick}
    >
      <div className={styles.thumbnailContainer}>
        <img
          className={styles.thumbnailBlur}
          src={props.thumbnailUrl}
          alt=""
        />
        <div className={styles.contentContainer}>
          <img
            className={styles.thumbnail}
            src={props.thumbnailUrl}
            alt={title}
          />
          <div className={styles.overlay}>
            <div className={styles.header}>
              <CardHeader {...props} />
            </div>
            <OverlayContent {...props} title={title} />
          </div>
        </div>
      </div>

      {props.type === ShowCardType.Scheduled && (
        <Footer {...props} title={title} />
      )}
    </button>
  )
}
